import React, { ReactElement, useEffect, useState } from "react";
import { ButtonGroup } from "@boi/react-components";
import styled from "styled-components";

import { evaluates_as_hiddenV2 } from "../conditional-logic";
import { ID, replaceMergeTagsInString, stringHasValueTag } from "../helper";
import { FormContext, iChoice, iControlProps } from "../types";
import { ConditionalWrapper } from "../../theme/style-utils-coras-nua";

const RadioButtonContainer = styled("div")`
    display: block;
    width: 100%;
`;

export type RadioProps = iControlProps & {
    context?: FormContext;
    choices: iChoice[];
    id?: string | number;
};

const RadioButtonGroup = (props: RadioProps) => {
    const [selected, setSelected] = useState(
        props.context?.getValue(`${props.name}`) || undefined
    );

    useEffect(() => {
        if (!props.context?.getValue(`${props.name}`)) {
            setSelected(undefined);
        }
    }, [props]);

    const rid = ID();

    const choices = (props.choices || []).reduce(
        (
            options: {
                label: string;
                value: string;
                id: string;
            }[],
            choice: iChoice,
            idx: number
        ) => {
            let hidden = false;
            if (props.context && choice.conditionalLogic) {
                hidden = evaluates_as_hiddenV2(
                    choice.conditionalLogic,
                    props.context.state
                );
            }
            if (!hidden) {
                let tempLabel = choice.text;
                if (!stringHasValueTag(choice.value)) {
                    tempLabel = replaceMergeTagsInString(
                        choice.text,
                        props.context ? props.context.getValues() : {}
                    );
                }

                return [
                    ...options,
                    {
                        label: tempLabel,
                        value: choice.value,
                        id: `radio[${rid}][${idx}]`,
                        checked: choice.value === selected
                    }
                ];
            } else {
                if (choice.value === props.context?.getValue(`${props.name}`)) {
                    props.context?.unsetValue(`${props.name}`);
                }
                return options;
            }
        },
        []
    );

    let Container = RadioButtonContainer;
    let useContainer = false;

    const libraryVariant = (() => {
        if (props.cssClass?.includes("three-by-three")) {
            useContainer = true;
            Container = styled(RadioButtonContainer)`
                fieldset > div {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 8px;
                }
                @media (max-width: 767px) {
                    fieldset > div {
                        grid-template-columns: 1fr;
                    }
                }
            `;
            return undefined;
        }
        if (props.cssClass?.includes("all-options-inline")) {
            useContainer = true;
            const isLastElementOnNewLine = props.cssClass?.includes(
                "all-options-inline-except-last"
            );

            if (isLastElementOnNewLine) {
                const columns = (choices.length - 1) % 8;
                Container = styled(RadioButtonContainer)`
                    fieldset > div {
                        display: flex;
                        flex-wrap: wrap;
                    }
                    fieldset > div > label {
                        display: block;
                        min-width: unset;
                        width: calc(
                            (100% / ${columns}) -
                                ((1 - 1 / ${columns}) * 0.25rem)
                        );
                    }
                    fieldset > div > label:last-child {
                        width: 100%;
                    }
                    fieldset > div > label > span {
                        min-height: 48px;
                        display: block;
                        align-content: center;
                    }

                    @media (max-width: 767px) {
                        fieldset > div > label {
                            width: 100%;
                        }
                    }
                `;
            } else {
                Container = styled(RadioButtonContainer)`
                    fieldset > div > label {
                        min-width: unset;
                    }

                    @media (max-width: 767px) {
                        fieldset > div {
                            flex-wrap: wrap;
                        }
                        fieldset > div > label {
                            width: 100%;
                        }
                    }
                `;
            }

            return "stretch";
        }

        if (props.cssClass?.includes("all-options-stacked")) {
            const leftAlign = props.cssClass?.includes("align-left");

            useContainer = true;
            Container = leftAlign
                ? styled(RadioButtonContainer)`
                      fieldset > div {
                          width: 100%;
                      }
                      fieldset > div > label {
                          min-width: 142px;
                          max-width: 516px;
                      }
                      @media (max-width: 767px) {
                          fieldset > div,
                          fieldset > div > label {
                              max-width: 100%;
                              width: 100%;
                          }
                      }
                  `
                : styled(RadioButtonContainer)`
                      fieldset > div > label > span {
                          width: 100%;
                          text-align: center;
                      }
                  `;
            return "stacked";
        }

        if (props.cssClass?.includes("aling-left")) {
            return "left";
        }

        useContainer = true;
        Container = styled(RadioButtonContainer)`
            fieldset > div {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 8px;
            }

            @media (max-width: 767px) {
                fieldset > div {
                    grid-template-columns: 1fr;
                }
            }
        `;

        return undefined;
    })();

    return (
        <ConditionalWrapper
            condition={useContainer}
            wrapper={(children: ReactElement) => (
                <Container>{children}</Container>
            )}
        >
            <ButtonGroup
                items={choices}
                name={props.name || `radio[${rid}]`}
                variant={libraryVariant}
                onChange={e => {
                    setSelected(e.target.value);
                    props.onChange && props.onChange(e.target.value);
                }}
            />
        </ConditionalWrapper>
    );
};

RadioButtonGroup.defaultProps = {
    choices: []
};

export default RadioButtonGroup;
